import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials: the session lives in an httpOnly cookie set by the backend.
export const api = axios.create({ baseURL: API, timeout: 300000, withCredentials: true });

let unauthorizedHandler = null;
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = fn;
};

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error?.config?.url || "";
    if (error?.response?.status === 401 && !url.includes("/auth/") && unauthorizedHandler) {
      unauthorizedHandler();
    }
    return Promise.reject(error);
  }
);

export function errMessage(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return d[0]?.msg || fallback;
  if (e?.code === "ECONNABORTED") return "The request timed out. Try fewer or smaller files.";
  return e?.message || fallback;
}

/* ------------------------------------ auth ----------------------------------- */
export const authConfig = async () => (await api.get("/auth/config")).data;
export const authMe = async () => (await api.get("/auth/me")).data;
export const authExchange = async (session_id) => (await api.post("/auth/session", { session_id })).data;
export const authLogout = async () => (await api.post("/auth/logout")).data;
export const listUsers = async () => (await api.get("/auth/users")).data;
export const updateUser = async (userId, patch) => (await api.put(`/auth/users/${userId}`, patch)).data;
export const removeUser = async (userId) => (await api.delete(`/auth/users/${userId}`)).data;

/* ------------------------------------ audit ---------------------------------- */
export const listAudit = async (params = {}) => (await api.get("/audit", { params })).data;

/* ----------------------------- manual extraction ----------------------------- */
export const extractVendors = async ({ files = [], emailBody = "" }) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f, f.name));
  form.append("email_body", emailBody || "");
  const { data } = await api.post("/extract", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const listBatches = async (q = "") =>
  (await api.get("/batches", { params: { q, limit: 100 } })).data;
export const getBatch = async (id) => (await api.get(`/batches/${id}`)).data;
export const saveBatchVendors = async (id, vendors) =>
  (await api.put(`/batches/${id}`, { vendors })).data;
export const deleteBatch = async (id) => (await api.delete(`/batches/${id}`)).data;
export const reextractBatch = async (id) => (await api.post(`/batches/${id}/reextract`)).data;
export const pushBatchToQueue = async (id) => (await api.post(`/queue/from_batch/${id}`)).data;
export const getStats = async () => (await api.get("/stats")).data;
export const fileUrl = (batchId, fileId) => `${API}/batches/${batchId}/files/${fileId}`;
export const batchCsvUrl = (batchId) => `${API}/batches/${batchId}/export`;

/* ---------------------------------- gmail ---------------------------------- */
export const gmailStatus = async () => (await api.get("/gmail/status")).data;
export const gmailOauthStart = async () => (await api.get("/gmail/oauth/start")).data;
export const gmailSync = async (forceFull = false) =>
  (await api.post("/gmail/sync", null, { params: { force_full: forceFull } })).data;
export const gmailDisconnect = async (purgeMail = false) =>
  (await api.post("/gmail/disconnect", null, { params: { purge_mail: purgeMail } })).data;
export const gmailStartWatch = async () => (await api.post("/gmail/watch")).data;
export const gmailStopWatch = async () => (await api.post("/gmail/watch/stop")).data;
export const gmailPubsubEvents = async () => (await api.get("/gmail/pubsub/events")).data;

/* ---------------------------------- inbox ---------------------------------- */
export const listInbox = async (params = {}) => (await api.get("/inbox", { params })).data;
export const getThread = async (id) => (await api.get(`/inbox/${id}`)).data;
export const classifyThread = async (id) => (await api.post(`/inbox/${id}/classify`)).data;
export const refreshThread = async (id) => (await api.post(`/inbox/${id}/refresh`)).data;
export const extractThread = async (id) => (await api.post(`/inbox/${id}/extract`)).data;
export const reclassifyAll = async () => (await api.post("/inbox/reclassify")).data;
export const attachmentUrl = (path) => `${BACKEND_URL}${path}`;

/* --------------------- automatic extraction of approvals -------------------- */
export const autoExtractStatus = async (jobId) =>
  (await api.get("/inbox/auto_extract/status", { params: jobId ? { job_id: jobId } : {} })).data;
export const startAutoExtractBulk = async ({ retryFailed = false, limit = 500 } = {}) =>
  (await api.post("/inbox/auto_extract/bulk", { retry_failed: retryFailed, limit })).data;
export const cancelAutoExtract = async (jobId) =>
  (await api.post("/inbox/auto_extract/cancel", null, { params: jobId ? { job_id: jobId } : {} })).data;
export const autoExtractThread = async (id) => (await api.post(`/inbox/${id}/auto_extract`)).data;

/* ---------------------------------- queue ---------------------------------- */
export const listQueue = async (params = {}) => (await api.get("/queue", { params })).data;
export const createQueueRow = async (values = {}) => (await api.post("/queue", { values })).data;
export const updateQueueRow = async (id, values) => (await api.put(`/queue/${id}`, { values })).data;
export const deleteQueueRow = async (id) => (await api.delete(`/queue/${id}`)).data;
export const bulkDeleteQueueRows = async (row_ids) =>
  (await api.post("/queue/bulk_delete", { row_ids })).data;
export const resetExportFlag = async (row_ids) =>
  (await api.post("/queue/reset_export", { row_ids })).data;

/* ---------------------------------- export ---------------------------------- */
export const exportPreview = async (row_ids) => (await api.post("/export/preview", { row_ids })).data;
export const exportDownload = async (row_ids, apply_label) =>
  api.post("/export/download", { row_ids, apply_label }, { responseType: "blob" });
export const exportRuns = async () => (await api.get("/export/runs")).data;

/* --------------------------------- settings --------------------------------- */
export const getSettings = async () => (await api.get("/settings")).data;
export const putSettings = async (patch) => (await api.put("/settings", patch)).data;
export const resetCsvFormat = async () => (await api.post("/settings/reset_csv_format")).data;
